/**
 * Store Config Transformer
 * Orchestrates transformation of complete store configuration
 * Single Responsibility: Orchestrating store config transformation using other transformers
 *
 * This transformer uses composition to delegate specific transformations
 * to specialized transformers, following the Single Responsibility Principle
 */

import { transformProducts } from "./productTransformer";
import { transformCategories } from "./categoryTransformer";

function toCamelSettingKey(key) {
  return key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

function transformOrderLiveSettings(rawSettings = {}) {
  const settings = Object.entries(rawSettings).reduce((acc, [key, value]) => {
    acc[key] = value;
    acc[toCamelSettingKey(key)] = value;
    return acc;
  }, {});

  return {
    ...settings,
    ooTips: rawSettings.oo_tips !== false,
    oo_tips: rawSettings.oo_tips !== false,
    ooBuyNow: rawSettings.oo_buynow === true,
    oo_buynow: rawSettings.oo_buynow === true,
    ooHeaderScript: rawSettings.oo_headerscript || "",
    oo_headerscript: rawSettings.oo_headerscript || ""
  };
}

/**
 * Transforms store configuration response
 * Uses composition to delegate to specialized transformers
 * @param {Object} rawConfig - Raw config response from API
 * @returns {Object} Transformed store config object
 */
export function transformStoreConfig(rawConfig) {
  // Delegate category transformation to category transformer
  const categories = transformCategories(rawConfig.categories);

  // Delegate product transformation to product transformer
  const products = transformProducts(rawConfig.products);
  const orderLiveSettings = transformOrderLiveSettings(rawConfig.settings);

  return {
    store: {
      id: rawConfig.store?.id,
      name: rawConfig.store?.name || "",
      displayName:
        rawConfig.store?.display_name || rawConfig.store?.displayName || "",
      phone: rawConfig.store?.phone || "",
      logo: rawConfig.store?.logo || "",
      timezone: rawConfig.store?.timezone || "",
      taxRound: rawConfig.store?.tax_round || false,
      taxOption: rawConfig.store?.tax_option || false,
      address: rawConfig.store?.address || "",
      city: rawConfig.store?.city || "",
      state: rawConfig.store?.state || "",
      zipcode: rawConfig.store?.zipcode || "",
      startTime: rawConfig.store?.start_time || "",
      closeTime: rawConfig.store?.close_time || "",
      orderLiveSettings,
      ...rawConfig.store
    },
    settings: orderLiveSettings,
    categories,
    products,
    diningOptions: Array.isArray(rawConfig.dining_options)
      ? rawConfig.dining_options
      : [],
    paymentTypes: Array.isArray(rawConfig.payment_types)
      ? rawConfig.payment_types
      : []
  };
}
